import { createPortal } from "react-dom";

/**
 * Read-only popup for an employee to check on their own proposed tasks —
 * what's still awaiting review vs. already approved/rejected, capped to the
 * last two weeks server-side. Same visual language as TaskProposalsModal
 * (the admin's review popup), no approve/reject actions here.
 */
const fmt = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

const STATUS = {
  pending: { label: "Awaiting review", pill: "pill-update", row: "diff-update" },
  approved: { label: "Approved", pill: "pill-add", row: "diff-add" },
  rejected: { label: "Rejected", pill: "pill-del", row: "diff-del" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

function EstimateBadge({ item, currencySymbol, timeUnit }) {
  if (item.client_estimate_price !== null && item.client_estimate_price !== undefined) {
    return <span className="diff-val">{currencySymbol}{fmt(item.client_estimate_price)}</span>;
  }
  if (item.client_estimate_hours !== null && item.client_estimate_hours !== undefined) {
    return <span className="diff-val">{fmt(item.client_estimate_hours)} {timeUnit}</span>;
  }
  return <span className="diff-val empty">no estimate</span>;
}

export default function MyProposalsModal({
  open,
  proposals = [],
  currencySymbol = "$",
  timeUnit = "hours",
  busy = false,
  onClose,
}) {
  if (!open) return null;

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card inbound-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 640 }}
      >
        <h3>Your task requests</h3>
        <p className="muted" style={{ marginTop: 8 }}>
          What you've proposed on this page in the last two weeks, and where
          each request stands.
        </p>

        <div className="inbound-body">
          {busy && <p className="muted-sm">Loading…</p>}
          {!busy && proposals.length === 0 && (
            <p className="muted-sm">Nothing proposed here in the last two weeks.</p>
          )}
          {!busy && proposals.map((p) => {
            const s = STATUS[p.status] || STATUS.pending;
            return (
              <section key={p.id} style={{ marginBottom: 18 }}>
                <h4 className="diff-section" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className={`pill ${s.pill}`}>{s.label}</span>
                  <span className="muted-sm">
                    {fmtDate(p.created_at)} · {p.items.length} task{p.items.length === 1 ? "" : "s"}
                  </span>
                </h4>
                {p.items.map((item, i) => (
                  <div className={`diff-row ${s.row}`} key={i}>
                    <div className="diff-head">
                      <span className={`diff-name${p.status === "rejected" ? " strike" : ""}`}>
                        {item.url ? (
                          <a href={item.url} target="_blank" rel="noreferrer">{item.name}</a>
                        ) : (
                          item.name
                        )}
                      </span>
                    </div>
                    <div className="diff-fields">
                      <div className="diff-field">
                        <span className="diff-label">Estimate</span>
                        <EstimateBadge item={item} currencySymbol={currencySymbol} timeUnit={timeUnit} />
                      </div>
                    </div>
                  </div>
                ))}
              </section>
            );
          })}
        </div>

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20 }}>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
